import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, inject } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { ApiService } from "../../core/api.service";
import { AuthService } from "../../core/auth.service";
import { I18nService } from "../../core/i18n.service";

@Component({
  selector: "sb-login",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  template: `
    <section class="login-wrap">
      <article class="login-shell">
        <aside class="brand-pane">
          <div class="brand-hero">
            <img src="assets/media/smartbirds.png" alt="SmartBirds" />
            <h1>SmartBirds</h1>
          </div>
          <h2>{{ 'login.signIn' | translate }}</h2>
          <p>{{ 'login.signInHint' | translate }}</p>
          <div class="visual">
            <div class="orb orb-a"></div>
            <div class="orb orb-b"></div>
            <div class="orb orb-c"></div>
          </div>
        </aside>

        <div class="form-pane">
          <div class="form-header">
            <h2>{{ 'login.title' | translate }}</h2>
            <p>{{ 'login.subtitle' | translate }}</p>
          </div>

          <form [formGroup]="form" (ngSubmit)="submit()">
            <label class="form-label">
              {{ 'login.email' | translate }}
              <input class="form-control" formControlName="email" type="email" [placeholder]="'login.emailPlaceholder' | translate" />
            </label>

            <label class="form-label mt-2">
              {{ 'login.password' | translate }}
              <div class="password-wrap">
                <input class="form-control" formControlName="password" [type]="showPassword ? 'text' : 'password'" [placeholder]="'login.passwordPlaceholder' | translate" />
                <button class="toggle-pass" type="button" (click)="showPassword = !showPassword">
                  {{ showPassword ? ('login.hide' | translate) : ('login.show' | translate) }}
                </button>
              </div>
            </label>

            <button class="btn btn-primary w-100 mt-3 login-submit" type="submit" [disabled]="form.invalid || loading">
              {{ loading ? ('login.loading' | translate) : ('login.submit' | translate) }}
            </button>
          </form>

          <div class="alert alert-danger mt-3 mb-0" *ngIf="error">{{ error }}</div>
        </div>
      </article>
    </section>
  `,
  styles: [
    `
      .login-wrap {
        display: grid;
        place-items: center;
        min-height: 100vh;
        padding: 1.25rem;
        background:
          radial-gradient(circle at 0% 0%, rgba(59, 130, 246, 0.08), transparent 40%),
          radial-gradient(circle at 100% 100%, rgba(14, 165, 233, 0.08), transparent 36%),
          #f8fafc;
      }
      .login-shell {
        width: min(960px, 96vw);
        display: grid;
        grid-template-columns: 1fr 1fr;
        border: 1px solid #e2e8f0;
        border-radius: 16px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
      }
      .brand-pane {
        position: relative;
        padding: 2.1rem;
        background: linear-gradient(160deg, #0f172a 0%, #1e3a8a 52%, #0284c7 100%);
        color: #e2e8f0;
      }
      .brand-hero {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        margin-bottom: 1rem;
      }
      .brand-hero img {
        width: 72px;
        height: 72px;
        object-fit: contain;
      }
      .brand-hero h1 {
        margin: 0;
        font-size: 2rem;
        line-height: 1.05;
        letter-spacing: 0.01em;
        color: #f8fafc;
      }
      .brand-pane h2 {
        margin: 0;
        font-size: 1.35rem;
        line-height: 1.25;
        color: #f8fafc;
      }
      .brand-pane p {
        margin: 0.6rem 0 1.1rem;
        color: rgba(226, 232, 240, 0.95);
      }
      .visual {
        position: relative;
        margin-top: 1.55rem;
        height: 120px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.07);
        backdrop-filter: blur(2px);
      }
      .orb {
        position: absolute;
        border-radius: 999px;
      }
      .orb-a {
        width: 58px;
        height: 58px;
        left: 14px;
        top: 16px;
        background: rgba(56, 189, 248, 0.55);
      }
      .orb-b {
        width: 42px;
        height: 42px;
        left: 78px;
        top: 42px;
        background: rgba(134, 239, 172, 0.5);
      }
      .orb-c {
        width: 72px;
        height: 72px;
        right: 24px;
        top: 24px;
        background: rgba(165, 180, 252, 0.45);
      }
      .form-pane {
        padding: 2.1rem;
      }
      .form-header h2 {
        margin: 0;
        font-size: 1.35rem;
        color: #111827;
        font-weight: 700;
      }
      .form-header p {
        margin: 0.32rem 0 1.25rem;
        color: #64748b;
        font-size: 0.9rem;
      }
      .form-label {
        display: block;
        width: 100%;
        font-weight: 600;
        color: #475569;
        font-size: 0.83rem;
        letter-spacing: 0.01em;
      }
      form {
        width: 100%;
      }
      .form-control {
        margin-top: 0.35rem;
        display: block;
        height: 46px;
        min-height: 46px;
        border-radius: 10px;
        border-color: #dbe2ea;
        font-size: 0.92rem;
        width: 100%;
        box-sizing: border-box;
      }
      .form-control:focus {
        border-color: #60a5fa;
        box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.18);
      }
      .password-wrap {
        position: relative;
        margin-top: 0.35rem;
      }
      .password-wrap .form-control {
        margin-top: 0;
        height: 46px;
        min-height: 46px;
        padding-right: 4.3rem;
      }
      .toggle-pass {
        position: absolute;
        top: 50%;
        right: 0.5rem;
        transform: translateY(-50%);
        border: 0;
        background: transparent;
        color: #3b82f6;
        font-weight: 600;
        font-size: 0.78rem;
      }
      .login-submit {
        height: 46px;
        min-height: 46px;
        font-weight: 700;
        border-radius: 10px;
        background: linear-gradient(90deg, #2563eb 0%, #3b82f6 100%);
        border: 0;
      }
      .login-submit:disabled {
        opacity: 0.7;
      }
      @media (max-width: 860px) {
        .login-shell {
          grid-template-columns: 1fr;
        }
        .brand-pane {
          padding: 1.3rem;
        }
        .form-pane {
          padding: 1.35rem;
        }
        .visual {
          display: none;
        }
      }
    `
  ]
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly cdr = inject(ChangeDetectorRef);

  loading = false;
  error = "";
  showPassword = false;

  form = this.fb.group({
    email: ["", [Validators.required]],
    password: ["", [Validators.required, Validators.minLength(6)]]
  });

  submit(): void {
    if (this.form.invalid || this.loading) {
      return;
    }

    this.loading = true;
    this.error = "";
    this.flushUi();

    const email = this.form.controls.email.value ?? "";
    const password = this.form.controls.password.value ?? "";

    this.api.login(email, password).subscribe({
      next: (response) => {
        this.auth.setSession({
          token: response.accessToken,
          role: response.role,
          permissions: response.permissions,
          username: response.username,
          displayName: response.displayName,
          avatarUrl: response.avatarUrl
        });
        this.flushUi();
        this.router.navigateByUrl("/admin/devices");
      },
      error: () => {
        this.error = this.i18n.t("login.invalidCredentials");
        this.loading = false;
        this.flushUi();
      },
      complete: () => {
        this.loading = false;
        this.flushUi();
      }
    });
  }

  private flushUi(): void {
    this.cdr.detectChanges();
  }
}
